import { NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useState } from 'react'
import { generatePath, Link, useParams } from 'react-router-dom'

import { useFetchAllStorageContainers } from '@@partner:graphql/query/storageContainer/allStorageContainers.gql'

import { AddOutline, CopyOutline, PrintOutline } from 'react-ionicons'
import { FlexboxGrid, Nav, Navbar, Pagination, SelectPicker, Modal } from 'rsuite'

import StorageContainersTable from '@components/storageContainer/table/StorageContainersTable'
import StorageContainersFilter from '@components/storageContainer/table/StorageContainersFilter'
import { BatchCreateStorageContainersForm } from '@components/storageContainer/form/BatchCreateStorageContainersForm'
import ContainerLabelPrinter from '@components/storageContainer/print/ContainerLabelPrinter'
import ShopTopMenu from '@components/shop/ShopTopMenu'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import { useGridHelper } from '@@partner:helpers/crud/grid'

import { ROUTE_STORAGE_CONTAINERS_CREATE } from '@@partner:config/routes'

export default function StorageContainersPage () {
  const { brandSlug } = useParams<any>()

  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })

  const [ showBatchCreate, setShowBatchCreate ] = useState(false)
  const [ selectedContainerIds, setSelectedContainerIds ] = useState<string[]>([])
  const [ showPrintModal, setShowPrintModal ] = useState(false)

  const {
    page,
    limit,
    defaultLimitOptions,
    tableSort,
    handlePageSelect,
    handleLimitSelect,
    handleSortColumn
  } = useGridHelper()

  const { data, loading, error, refetch } = useFetchAllStorageContainers({
    pager: {
      page,
      limit
    },
    filter,
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    }
  })

  const handleFilterChanged = useCallback((updatedFilter: NestedFilter) => {
    setFilter(updatedFilter)
  }, [])

  const containers = data?.allStorageContainers?.edges ?? []
  const pageInfo = data?.allStorageContainers?.pageInfo

  const handleRefresh = useCallback(() => {
    refetch()
  }, [ refetch ])

  const handlePrintSelected = () => {
    if (selectedContainerIds.length === 0) {
      return
    }
    setShowPrintModal(true)
  }

  const selectedContainers = containers.filter(c => 
    selectedContainerIds.includes(String(c.id))
  )

  if (error) {
    return <GraphQLCommonErrorDisplay error={error} />
  }

  return (
    <div>
      <h1 className='u-heading'>Управление контейнерами</h1>
      <ShopTopMenu />
      
      <Navbar appearance='subtle'>
        <Nav>
          <Nav.Item
            icon={<AddOutline />}
            as={Link}
            to={generatePath(ROUTE_STORAGE_CONTAINERS_CREATE, { brandSlug })}
          >
            Создать контейнер
          </Nav.Item>
          <Nav.Item
            icon={<CopyOutline />}
            onClick={() => setShowBatchCreate(true)}
          >
            Массовое создание
          </Nav.Item>
          {selectedContainerIds.length > 0 && (
            <Nav.Item
              icon={<PrintOutline />}
              onClick={handlePrintSelected}
            >
              Печать этикеток ({selectedContainerIds.length})
            </Nav.Item>
          )}
        </Nav>
      </Navbar>

      <StorageContainersFilter
        filter={filter}
        onFilterChanged={handleFilterChanged}
      />

      <div style={{ marginTop: 20 }}>
        <StorageContainersTable
          brandSlug={brandSlug}
          containers={containers}
          loading={loading}
          onSortColumn={handleSortColumn}
          tableSort={tableSort}
          onRefresh={handleRefresh}
          selectedContainers={selectedContainerIds}
          onSelectionChange={setSelectedContainerIds}
        />

        {pageInfo && (
          <div style={{ marginTop: 20 }}>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Pagination
                  prev
                  next
                  first
                  last
                  ellipsis
                  boundaryLinks
                  maxButtons={5}
                  size="md"
                  layout={[ 'total', '-', 'limit', '|', 'pager', 'skip' ]}
                  total={pageInfo.total}
                  limitOptions={defaultLimitOptions}
                  limit={limit}
                  activePage={page}
                  onChangePage={handlePageSelect}
                  onChangeLimit={handleLimitSelect}
                />
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </div>
        )}
      </div>

      <BatchCreateStorageContainersForm
        open={showBatchCreate}
        onClose={() => setShowBatchCreate(false)}
        onSuccess={handleRefresh}
      />

      {showPrintModal && (
        <Modal
          open={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          size="lg"
        >
          <Modal.Header>
            <Modal.Title>Печать этикеток контейнеров</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ContainerLabelPrinter
              containers={selectedContainers}
              onClose={() => setShowPrintModal(false)}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  )
}
